// Bots & API-Tokens: Hilfen ohne React (Validierung, Scope-Texte, Ablauf, Fehlertexte).
import { API_SCOPES, type ApiScope } from '@lagerfeuer/shared';
import { ApiError } from '../../../lib/api';
import { errorMessage } from '../format';

export const BOT_USERNAME_RE = /^[a-z0-9_.]{2,32}$/;
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const IMAGE_ACCEPT = IMAGE_TYPES.join(',');

export function isAllowedImage(file: File): boolean {
  return (IMAGE_TYPES as readonly string[]).includes(file.type);
}

/** Deutsche Beschreibungen der Token-Scopes (Reihenfolge = API_SCOPES). */
export const SCOPE_LABELS: Record<ApiScope, string> = {
  '*': 'Vollzugriff – alles, was der Besitzer darf',
  'messages.read': 'Nachrichten lesen',
  'messages.write': 'Nachrichten senden, bearbeiten und löschen',
  'channels.read': 'Kanäle lesen',
  'channels.write': 'Kanäle anlegen und ändern',
  'servers.read': 'Server-Infos lesen',
  'servers.write': 'Server-Einstellungen ändern',
  'members.read': 'Mitglieder lesen',
  'members.write': 'Mitglieder verwalten (Rollen, Spitznamen, Kicks)',
  'reactions.write': 'Reaktionen setzen und entfernen',
  'webhooks.manage': 'Webhooks verwalten',
  'users.read': 'Nutzerprofile lesen',
  gateway: 'Echtzeit-Verbindung (Socket.IO-Ereignisse)',
};

export const ALL_SCOPES: readonly ApiScope[] = API_SCOPES;

/** Scope an-/abwählen; `*` schließt alle anderen aus und umgekehrt. */
export function toggleScope(current: readonly ApiScope[], scope: ApiScope, checked: boolean): ApiScope[] {
  if (!checked) return current.filter((s) => s !== scope);
  if (scope === '*') return ['*'];
  return [...current.filter((s) => s !== '*' && s !== scope), scope];
}

export type ExpiryChoice = 'never' | '7' | '30' | '90' | 'date';

/**
 * Ablauf-Auswahl → ISO-Zeitpunkt. `undefined` = läuft nie ab. Datum (`YYYY-MM-DD`) gilt bis Tagesende (lokal).
 * Wirft bei fehlendem/vergangenem Datum einen Fehlertext.
 */
export function expiryToIso(choice: ExpiryChoice, date: string, now: number = Date.now()): string | undefined {
  if (choice === 'never') return undefined;
  if (choice === 'date') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!m) throw new Error('Bitte ein Ablaufdatum wählen.');
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59);
    if (Number.isNaN(d.getTime()) || d.getTime() <= now) throw new Error('Das Ablaufdatum muss in der Zukunft liegen.');
    return d.toISOString();
  }
  return new Date(now + Number(choice) * 86_400_000).toISOString();
}

export function isExpired(iso: string | null, now: number = Date.now()): boolean {
  return !!iso && new Date(iso).getTime() <= now;
}

/** Fehlertext mit verständlichen Meldungen für Limits/Rechte/Namenskonflikte. */
export function botsErrorText(err: unknown, what: 'bot' | 'token' | 'server' = 'bot'): string {
  if (err instanceof ApiError) {
    if (err.code === 'LIMIT_REACHED') {
      return what === 'token'
        ? 'Du hast die maximale Anzahl an API-Tokens erreicht. Widerrufe zuerst einen alten Token.'
        : 'Du hast die maximale Anzahl an Bots erreicht. Lösche zuerst einen Bot.';
    }
    if (err.code === 'USERNAME_TAKEN') return 'Dieser Benutzername ist schon vergeben.';
    if (what === 'server' && (err.status === 403 || err.status === 404)) {
      return 'Du brauchst im gewählten Server das Recht „Server verwalten", um einen Bot hinzuzufügen.';
    }
  }
  return errorMessage(err);
}

/** React-Query-Schlüssel. */
export const botsKey = ['extras', 'bots'] as const;
export const tokensKey = (ownerId?: string) => ['extras', 'tokens', ownerId ?? 'me'] as const;
export const serversLookupKey = ['extras', 'lookup', 'servers'] as const;
